import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useOperationStore from '../../store/useOperationStore';
import useAuthStore from '../../store/authStore';
import DocumentListWidget from '../../components/documents/DocumentListWidget';
import OperationSurveyPricingCard from '../../components/operations/OperationSurveyPricingCard';
import OperationStageTab from '../../components/operations/OperationStageTab';
import OperationInvoicesTab from '../../components/operations/OperationInvoicesTab';
import OperationFinanceTab from '../../components/operations/OperationFinanceTab';
import OperationInvoiceAttachModal from '../../components/modals/OperationInvoiceAttachModal';
import InvoiceDetailModal from '../../components/modals/InvoiceDetailModal';
import OperationAddressBlock from '../../components/operations/OperationAddressBlock';
import { createEmptyOperationAddress, normalizeOperationAddressValue } from '../../components/operations/operationAddressUtils';

const statuses = ['Pending', 'In_Transit', 'Customs', 'Delivered', 'Cancelled'];
const statusLabels = {
  Pending: 'Pending',
  In_Transit: 'In Transit',
  Customs: 'Customs',
  Delivered: 'Delivered',
  Cancelled: 'Cancelled'
};

const statusClass = {
  Pending: 'bg-amber-50 text-amber-700 border-amber-100',
  In_Transit: 'bg-blue-50 text-blue-700 border-blue-100',
  Customs: 'bg-purple-50 text-purple-700 border-purple-100',
  Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  Cancelled: 'bg-rose-50 text-rose-700 border-rose-100'
};

const operationTypeLabels = {
  None: 'Yok',
  Import: 'İthalat',
  Export: 'İhracat',
  Inland: 'İç Nakliye'
};

const transportModeLabels = {
  None: 'Yok',
  Air: 'Hava',
  Sea: 'Deniz',
  Land: 'Kara'
};

const serviceTypeLabels = {
  None: 'Yok',
  Delivery: 'Teslimat',
  International_Moving: 'Uluslararası Taşımacılık',
  Origin_Service: 'Çıkış Hizmeti',
  Domestic_Moving: 'Yurtiçi Evden Eve Taşımacılık',
  Domestic_Transportation: 'Yurtiçi Nakliye',
  Storage: 'Depolama'
};

const tabs = [
  { id: 'summary', label: 'Özet' },
  { id: 'invoices', label: 'Faturalar' },
  { id: 'origin', label: 'Origin' },
  { id: 'freight', label: 'Navlun' },
  { id: 'delivery', label: 'Teslimat' },
  { id: 'documents', label: 'Evraklar' },
  { id: 'finance', label: 'Karlılık Analizi' }
];


const formatOperationLabel = (value, labels, fallback = '-') => (
  labels[value] || value || fallback
);

const OperationDetail = () => {
  const params = useParams();
  const operationId = params.operationId || params.id;
  const navigate = useNavigate();
  const {
    currentOperation,
    currentOperationFinancialSummary,
    loading,
    operationFinancialSummaryLoading,
    fetchOperationById,
    fetchOperationFinancialSummary,
    updateOperationFields,
    updateOperationStatus,
    updateInvoiceOperation
  } = useOperationStore();
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const [activeTab, setActiveTab] = useState('summary');
  const [isInvoiceAttachModalOpen, setIsInvoiceAttachModalOpen] = useState(false);
  const [isInvoiceDetailModalOpen, setIsInvoiceDetailModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isPickupAddressSaving, setIsPickupAddressSaving] = useState(false);
  const [isDeliveryAddressSaving, setIsDeliveryAddressSaving] = useState(false);
  const [addressDrafts, setAddressDrafts] = useState({
    pickupAddress: createEmptyOperationAddress(),
    deliveryAddress: createEmptyOperationAddress()
  });
  const isFinancialSummaryLoading = operationFinancialSummaryLoading || (loading && !currentOperationFinancialSummary);
  const canWriteOperation = hasPermission('operation', 'write');
  const canAssignPersonnel = hasPermission('operation', 'assign');

  useEffect(() => {
    fetchOperationById(operationId);
    fetchOperationFinancialSummary(operationId);
  }, [
    operationId,
    fetchOperationById,
    fetchOperationFinancialSummary
  ]);

  useEffect(() => {
    if (!currentOperation) return;

    setAddressDrafts({
      pickupAddress: normalizeOperationAddressValue(currentOperation.pickupAddress),
      deliveryAddress: normalizeOperationAddressValue(currentOperation.deliveryAddress)
    });
  }, [currentOperation]);

  const handleStatusChange = async (status) => {
    if (!canWriteOperation) return toast.error('Operasyon durumu güncelleme yetkiniz yok.');

    const result = await updateOperationStatus(operationId, status);
    if (result.success) {
      toast.success('Operasyon durumu güncellendi');
    } else {
      toast.error(result.message);
    }
  };

  const handleAddressChange = (field) => (nextAddress) => {
    setAddressDrafts((current) => ({
      ...current,
      [field]: nextAddress
    }));
  };

  const handleAddressSave = async (field) => {
    if (!canWriteOperation) return toast.error('Adres güncelleme yetkiniz yok.');

    const savingField = field === 'pickupAddress'
      ? setIsPickupAddressSaving
      : setIsDeliveryAddressSaving;

    savingField(true);
    try {
      const result = await updateOperationFields(operationId, {
        [field]: addressDrafts[field]
      });

      if (result.success) {
        toast.success(field === 'pickupAddress' ? 'Alım adresi güncellendi' : 'Teslimat adresi güncellendi');
      } else {
        toast.error(result.message || 'Adres güncellenemedi');
      }
    } finally {
      savingField(false);
    }
  };

  const refreshOperation = async () => {
    await Promise.all([
      fetchOperationById(operationId),
      fetchOperationFinancialSummary(operationId)
    ]);
  };

  const handleOpenInvoice = (invoice) => {
    if (!invoice?._id) return;
    setSelectedInvoiceId(invoice._id);
    setIsInvoiceDetailModalOpen(true);
  };

  const handleAttachInvoice = async (invoice) => {
    if (!canWriteOperation || !invoice?._id) {
      return { success: false, message: 'Fatura ilişkilendirme yetkiniz yok.' };
    }

    const result = await updateInvoiceOperation(invoice._id, operationId);
    if (result.success) {
      toast.success('Fatura operasyona bağlandı');
      await fetchOperationFinancialSummary(operationId);
    } else {
      toast.error(result.message);
    }
    return result;
  };

  const handleDetachInvoice = async (invoice) => {
    if (!canWriteOperation || !invoice?._id) {
      return;
    }

    if (!window.confirm('Bu faturanın operasyon ilişkisini kaldırmak istediğinize emin misiniz?')) {
      return;
    }

    const result = await updateInvoiceOperation(invoice._id, null);
    if (result.success) {
      toast.success('Fatura operasyon ilişkisinden çıkarıldı');
      await fetchOperationFinancialSummary(operationId);
    } else {
      toast.error(result.message);
    }
  };

  if ((loading && !currentOperation) || (currentOperation && String(currentOperation._id) !== operationId)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={38} />
      </div>
    );
  }

  if (!currentOperation) {
      return (
      <div className="p-6 max-w-7xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/operations')}
          className="mb-5 inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-xs font-black text-slate-700 shadow-sm border border-slate-100"
        >
          <ArrowLeft size={16} /> Geri Dön
        </button>
        <div className="rounded-[2rem] border border-slate-100 bg-white p-10 text-center font-bold text-slate-400">
          Operasyon bulunamadı.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <button
            type="button"
            onClick={() => navigate('/operations')}
            className="inline-flex w-fit items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 transition-colors hover:bg-white"
          >
            <ArrowLeft size={16} /> Geri Dön
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900">{currentOperation.projectNo}</h1>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-400">
              {currentOperation.account?.name || 'Bağımsız Operasyon'}
            </p>
          </div>
        </div>
        <select
          value={currentOperation.status}
          onChange={(e) => handleStatusChange(e.target.value)}
          disabled={!canWriteOperation}
          className={`rounded-2xl border px-4 py-3 text-xs font-black outline-none ${statusClass[currentOperation.status] || 'border-slate-100 bg-slate-50 text-slate-600'}`}
        >
          {statuses.map((status) => (
            <option key={status} value={status}>{statusLabels[status]}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-2 rounded-[1.5rem] border border-slate-100 bg-white p-2 shadow-sm">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-2xl px-5 py-3 text-xs font-black transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="rounded-[2rem] border border-slate-100 bg-white p-4 shadow-sm lg:col-span-5">
            <div className="space-y-3">
              <div className="rounded-2xl bg-blue-50 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-500">Operasyon Tipi</p>
                <p className="mt-2 inline-flex min-w-0 max-w-full items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-black text-blue-700 shadow-sm">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                  <span className="min-w-0 truncate">
                    {formatOperationLabel(currentOperation.operationType, operationTypeLabels)}
                  </span>
                </p>
              </div>
              <div className="rounded-2xl bg-emerald-50 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Taşıma Modu</p>
                <p className="mt-2 inline-flex min-w-0 max-w-full items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-black text-emerald-700 shadow-sm">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                  <span className="min-w-0 truncate">
                    {formatOperationLabel(currentOperation.transportMode, transportModeLabels)}
                  </span>
                </p>
              </div>
              <div className="rounded-2xl bg-amber-50 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-500">Hizmet Türü</p>
                <p className="mt-2 inline-flex min-w-0 max-w-full items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-black text-amber-700 shadow-sm">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                  <span className="min-w-0 break-words whitespace-normal">
                    {formatOperationLabel(currentOperation.serviceType, serviceTypeLabels)}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <OperationSurveyPricingCard
            key={[
              currentOperation._id,
              currentOperation.surveyorName,
              currentOperation.estimatedVolume,
              currentOperation.pricingType,
              currentOperation.unitPrice,
              currentOperation.totalPrice,
              currentOperation.currency,
              currentOperation.pricePerM3,
              currentOperation.pricingAmount,
              currentOperation.priceCurrency
            ].join(':')}
            operation={currentOperation}
            canWrite={canWriteOperation}
          />
        </div>
      )}

      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <OperationAddressBlock
            title="Alım Adresi"
            description="Origin"
            value={addressDrafts.pickupAddress}
            onChange={handleAddressChange('pickupAddress')}
            onSave={() => handleAddressSave('pickupAddress')}
            saving={isPickupAddressSaving}
          />
          <OperationAddressBlock
            title="Teslimat Adresi"
            description="Destination"
            value={addressDrafts.deliveryAddress}
            onChange={handleAddressChange('deliveryAddress')}
            onSave={() => handleAddressSave('deliveryAddress')}
            saving={isDeliveryAddressSaving}
          />
        </div>
      )}

      {activeTab === 'invoices' && (
        <OperationInvoicesTab
          invoices={currentOperationFinancialSummary?.invoiceRows || []}
          loading={isFinancialSummaryLoading}
          onAddInvoice={() => setIsInvoiceAttachModalOpen(true)}
          onDetachInvoice={handleDetachInvoice}
          onOpenInvoice={handleOpenInvoice}
        />
      )}

      {['origin', 'freight', 'delivery'].includes(activeTab) && (
        <OperationStageTab
          key={operationId + activeTab}
          operation={currentOperation}
          stage={activeTab}
          invoices={currentOperationFinancialSummary?.invoiceRows || []}
          canAssign={canAssignPersonnel}
          canDeletePersonnel={hasPermission('personnel_expense', 'delete') || hasPermission('operation', 'delete')}
          expensesLoading={isFinancialSummaryLoading}
          expensesUnavailable={!currentOperationFinancialSummary && !isFinancialSummaryLoading}
          canExpense={canWriteOperation && hasPermission('invoice', 'write')}
          canConsume={canWriteOperation}
          consumptions={currentOperationFinancialSummary?.materialConsumptionRows || []}
          onRefresh={refreshOperation}
          onOpenInvoice={handleOpenInvoice}
        />
      )}

      {activeTab === 'documents' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
          <div className="xl:col-span-3">
            <DocumentListWidget category="OPERATION" parentResourceId={operationId} title="Operasyon Evrak Matrisi" />
          </div>

          <div className="space-y-6 xl:col-span-1">
            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Zorunlu Evrak Durumu</p>
              <div className="mt-3">
                {currentOperation?.requiredDocumentsStatus === 'Missing' ? (
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-rose-50 px-4 py-3 text-xs font-black text-rose-700">
                    <AlertTriangle size={16} /> Missing
                  </span>
                ) : currentOperation?.requiredDocumentsStatus === 'Complete' ? (
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-xs font-black text-emerald-700">
                    <CheckCircle2 size={16} /> Complete
                  </span>
                ) : (
                  <span className="inline-flex rounded-2xl bg-slate-50 px-4 py-3 text-xs font-black text-slate-500">
                    None
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'finance' && (
        <OperationFinanceTab
          summary={currentOperationFinancialSummary}
          loading={isFinancialSummaryLoading}
          onOpenInvoice={handleOpenInvoice}
        />
      )}

      <OperationInvoiceAttachModal
        isOpen={isInvoiceAttachModalOpen}
        operation={currentOperation}
        onClose={() => setIsInvoiceAttachModalOpen(false)}
        onAttachInvoice={handleAttachInvoice}
      />

      <InvoiceDetailModal
        isOpen={isInvoiceDetailModalOpen}
        onClose={() => {
          setIsInvoiceDetailModalOpen(false);
          setSelectedInvoiceId(null);
        }}
        invoiceId={selectedInvoiceId}
      />
    </div>
  );
};

export default OperationDetail;
